/**
 * Manage Vehicles — the fleet list, with the compliance state that Panel Quote
 * buried in a reminders column pulled out to where it can be scanned.
 *
 * Rows drag to reorder, which is also the order lanes appear in on the Gantt.
 */

import React, { useMemo, useState } from 'react';
import { Car, ChevronRight, Fuel, Settings2, Users } from 'lucide-react';

import { SortableList, arrayMove } from '../ui/sortable';
import { Skeleton } from '../ui/skeleton';
import {
  complianceItems,
  complianceSummary,
  formatCurrency,
  transmissionLabel,
  vehicleCategoryLabel,
  vehicleDescription,
  vehicleLabel,
  vehicleStateAt,
  VEHICLE_STATE_META
} from '../../lib/fleet';
import { COMPLIANCE_TONE_STYLES, VEHICLE_STATE_STYLES } from './fleetStyles';
import { cn } from '../../lib/utils';

const FleetVehiclesTable = ({
  vehicles,
  bookings,
  loading,
  now = new Date(),
  priv = { rego: (v) => v },
  onEdit,
  onReorder
}) => {
  const [order, setOrder] = useState(null);

  const ordered = useMemo(() => {
    if (!order) return vehicles;
    const map = new Map(vehicles.map((v) => [v.$id, v]));
    // Anything added since the local reorder falls in at the end.
    const known = order.map((id) => map.get(id)).filter(Boolean);
    const extra = vehicles.filter((v) => !order.includes(v.$id));
    return [...known, ...extra];
  }, [vehicles, order]);

  const handleReorder = (activeId, overId) => {
    const ids = ordered.map((v) => v.$id);
    const next = arrayMove(ids, ids.indexOf(activeId), ids.indexOf(overId));
    setOrder(next);
    onReorder?.(next.map((id) => ordered.find((v) => v.$id === id)).filter(Boolean));
  };

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 p-12 text-center">
        <Car className="h-10 w-10 text-muted-foreground" />
        <div>
          <p className="font-medium">No vehicles yet</p>
          <p className="text-sm text-muted-foreground">Add your loan cars and company vehicles to start taking bookings.</p>
        </div>
      </div>
    );
  }

  const renderRow = (id) => {
    const vehicle = ordered.find((v) => v.$id === id);
    if (!vehicle) return null;

    const state = vehicleStateAt(vehicle, bookings, now);
    const items = complianceItems(vehicle, now).filter(
      (item) => item.status === 'expired' || item.status === 'due_soon'
    );

    return (
      // The row is the target rather than a button at the end of it — you are
      // never scanning this list for anything but "open that one".
      <div
        role="button"
        tabIndex={0}
        onClick={() => onEdit?.(vehicle)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit?.(vehicle);
          }
        }}
        aria-label={`Edit ${vehicleLabel(vehicle)}`}
        className={cn(
          'flex cursor-pointer items-center gap-4 rounded-md border p-3 text-left transition-colors',
          'hover:border-primary/40 hover:bg-accent/40',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{vehicleLabel(vehicle)}</span>
            <span className="font-mono text-xs text-muted-foreground">{priv.rego(vehicle.registration)}</span>
            <span className={cn('rounded-full border px-1.5 py-0.5 text-[10px] font-medium', VEHICLE_STATE_STYLES[state])}>
              {VEHICLE_STATE_META[state]?.label}
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {[
              vehicleDescription(vehicle),
              vehicleCategoryLabel(vehicle.category),
              vehicle.odometer ? `${Number(vehicle.odometer).toLocaleString()} km` : null
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        <div className="hidden shrink-0 items-center gap-3 text-xs text-muted-foreground lg:flex">
          {vehicle.transmission && (
            <span className="inline-flex items-center gap-1" title="Transmission">
              <Settings2 className="h-3.5 w-3.5" />
              {transmissionLabel(vehicle.transmission)}
            </span>
          )}
          {vehicle.seats ? (
            <span className="inline-flex items-center gap-1" title="Seats">
              <Users className="h-3.5 w-3.5" />
              {vehicle.seats}
            </span>
          ) : null}
          {vehicle.fuelType && (
            <span className="inline-flex items-center gap-1 capitalize" title="Fuel">
              <Fuel className="h-3.5 w-3.5" />
              {vehicle.fuelType.replace('_', ' ')}
            </span>
          )}
        </div>

        <div className="hidden w-52 shrink-0 flex-wrap gap-1 md:flex">
          {vehicle.remindersEnabled === false ? (
            <span className="text-xs italic text-muted-foreground">Reminders off</span>
          ) : items.length === 0 ? (
            <span className="text-xs text-muted-foreground">All current</span>
          ) : (
            items.map((item) => (
              <span
                key={item.key}
                className={cn('rounded border px-1.5 py-0.5 text-[10px] font-medium', COMPLIANCE_TONE_STYLES[item.status])}
              >
                {item.label} {complianceSummary(item)}
              </span>
            ))
          )}
        </div>

        <div className="hidden w-24 shrink-0 text-right text-xs text-muted-foreground xl:block">
          {vehicle.dailyRate ? `${formatCurrency(vehicle.dailyRate)}/day` : '—'}
        </div>

        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    );
  };

  return (
    <div className="p-4">
      <SortableList ids={ordered.map((v) => v.$id)} onReorder={handleReorder} renderRow={renderRow} />
    </div>
  );
};

export default FleetVehiclesTable;
